/**
 * Session-flow dimensions for RUM-derived metrics.
 *
 * `finishTracking` tags session-flow actions with `operation`, `outcome`,
 * a semantic `error.type`, `client_platform` and, for recording operations,
 * `capture_transport`, so Datadog can derive the client session-flow attempt
 * metric. Every value is bounded. The Scribe vocabulary is registered by the app
 * at bootstrap (src/composition/wiring/runtime-wiring.ts); this module holds only the mechanism.
 */
import { type CaptureTransport, TrackingKey } from './types';

export type MetricOperation =
  | 'session_create'
  | 'recording_start'
  | 'recording_pause'
  | 'recording_resume'
  | 'recording_stop'
  | 'note_generate'
  | 'document_update'
  | 'document_generate'
  | 'context_upload'
  | 'note_update'
  | 'recording_finalize'
  | 'transcription_finalize';

// The OTel heidi.client.platform vocabulary.
// This app registers web or desktop, mobile clients emit ios or android.
export type ClientPlatform = 'web' | 'desktop' | 'ios' | 'android' | 'unknown';

/** Who started a generation. Only `explicit` emits intent. */
export type GenerateTrigger = 'explicit' | 'autonomous';

/** Contract outcome vocabulary. `abandoned` is emitted by the replay path. */
export type SessionFlowOutcome =
  | 'success'
  | 'failed'
  | 'blocked'
  | 'cancelled'
  | 'abandoned';

/** How a thrown value classifies. `errorType` is omitted for `cancelled`. */
export type SessionFlowFailure = {
  outcome: 'failed' | 'blocked' | 'cancelled';
  errorType?: string;
  /**
   * For a fault whose bucket depends on WHICH operation it broke: one writer
   * failure is an init fault on a resume and a stop fault on a pause. Total,
   * not `Partial`, so a new operation is a compile error, not a silent hole.
   */
  errorTypeByOperation?: Record<MetricOperation, string>;
};

const UNCLASSIFIED: SessionFlowFailure = { outcome: 'failed', errorType: '_OTHER' };

const METRIC_OPERATION_BY_KEY: Partial<Record<TrackingKey, MetricOperation>> = {
  [TrackingKey.SCRIBE_NEW_SESSION]: 'session_create',
  [TrackingKey.SCRIBE_START_RECORDING]: 'recording_start',
  [TrackingKey.SCRIBE_PAUSE_RECORDING]: 'recording_pause',
  [TrackingKey.SCRIBE_RESUME_RECORDING]: 'recording_resume',
  [TrackingKey.SCRIBE_STOP_RECORDING]: 'recording_stop',
  [TrackingKey.SCRIBE_NOTE_GENERATION]: 'note_generate',
  [TrackingKey.SCRIBE_DOCUMENT_UPDATE]: 'document_update',
  [TrackingKey.SCRIBE_DOCUMENT_GENERATION]: 'document_generate',
  [TrackingKey.SCRIBE_CONTEXT_UPLOAD]: 'context_upload',
  [TrackingKey.SCRIBE_NOTE_UPDATE]: 'note_update',
  [TrackingKey.SCRIBE_RECORDING_FINALIZE]: 'recording_finalize',
  [TrackingKey.SCRIBE_TRANSCRIPTION_FINALIZE]: 'transcription_finalize',
};

/** The `operation` dimension for a key, or `undefined` when the key has no metric. */
export function getMetricOperation(key: TrackingKey): MetricOperation | undefined {
  return METRIC_OPERATION_BY_KEY[key];
}

// Total, so a new operation is a compile error rather than a silently untagged one.
const CARRIES_CAPTURE_TRANSPORT = {
  session_create: false,
  recording_start: true,
  recording_pause: true,
  recording_resume: true,
  recording_stop: true,
  note_generate: false,
  document_update: false,
  document_generate: false,
  context_upload: false,
  note_update: false,
  recording_finalize: false,
  transcription_finalize: false,
} satisfies Record<MetricOperation, boolean>;

let clientPlatform: ClientPlatform | undefined;
let defaultCaptureTransport: CaptureTransport | undefined;
const failuresByName = new Map<string, SessionFlowFailure>();
const failuresByCode = new Map<string, SessionFlowFailure>();

/** One-time app-bootstrap call (src/composition/wiring/runtime-wiring.ts). */
export function registerClientPlatform(platform: ClientPlatform): void {
  clientPlatform = platform;
}

/**
 * One-time app-bootstrap call: the transport of a recording attempt no hook
 * stamped. A platform with a single transport (web) registers it; one with several
 * registers none, so an unstamped attempt stays visibly untagged.
 */
export function registerDefaultCaptureTransport(transport: CaptureTransport): void {
  defaultCaptureTransport = transport;
}

/**
 * The `capture_transport` tag: the stamped value, else the registered default.
 * `undefined` for a non-recording operation, or an unstamped attempt with no default.
 */
function resolveCaptureTransport(
  operation: MetricOperation | undefined,
  stamped: CaptureTransport | undefined
): CaptureTransport | undefined {
  if (!operation || !CARRIES_CAPTURE_TRANSPORT[operation]) return undefined;
  return stamped ?? defaultCaptureTransport;
}

/** One-time app-bootstrap call: the error-name to semantic-failure map. */
export function registerSessionFlowFailureTypes(
  map: Readonly<Record<string, SessionFlowFailure>>
): void {
  // Idempotent: a repeat call replaces the map, it does not accumulate.
  failuresByName.clear();
  for (const [name, failure] of Object.entries(map)) failuresByName.set(name, failure);
}

/**
 * One-time app-bootstrap call: the error-CODE to semantic-failure map. Desktop
 * failures all arrive as `IpcInvokeError`, so they share one `name` and the
 * semantics sit on `errorCode`; without this the fleet is a single `_OTHER`.
 */
export function registerSessionFlowFailureCodes(
  map: Readonly<Record<string, SessionFlowFailure>>
): void {
  failuresByCode.clear();
  for (const [code, failure] of Object.entries(map)) failuresByCode.set(code, failure);
}

/** The `client_platform` tag, or `undefined` before the app registers it. */
export function getClientPlatform(): ClientPlatform | undefined {
  return clientPlatform;
}

export type SessionFlowDimensions = {
  operation?: MetricOperation;
  client_platform?: ClientPlatform;
  capture_transport?: CaptureTransport;
};

/**
 * The tags every session-flow action carries, whether finished live or replayed
 * as abandoned. Empty for a key with no metric; unset tags are omitted.
 */
export function sessionFlowDimensions(
  key: TrackingKey,
  stampedTransport: CaptureTransport | undefined
): SessionFlowDimensions {
  const operation = getMetricOperation(key);
  if (!operation) return {};
  const platform = getClientPlatform();
  const captureTransport = resolveCaptureTransport(operation, stampedTransport);
  return {
    operation,
    ...(platform ? { client_platform: platform } : {}),
    ...(captureTransport ? { capture_transport: captureTransport } : {}),
  };
}

/**
 * Classifies a thrown value into a bounded outcome and error type. A Dexie
 * error hides its cause in `inner`, so an unmapped outer value falls back to
 * the inner one; anything still unrecognized is a failed `_OTHER`.
 */
export function classifySessionFlowFailure(
  error: unknown,
  operation?: MetricOperation
): SessionFlowFailure {
  const failure =
    lookupFailure(error) ??
    lookupFailure((error as { inner?: unknown } | null)?.inner) ??
    UNCLASSIFIED;
  const perOperation = operation ? failure.errorTypeByOperation?.[operation] : undefined;
  return perOperation ? { outcome: failure.outcome, errorType: perOperation } : failure;
}

/**
 * Name first, then code: a name is the more specific signal where both exist,
 * and the code carries desktop failures whose name is only ever the transport's.
 */
function lookupFailure(error: unknown): SessionFlowFailure | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const { name, errorCode } = error as { name?: unknown; errorCode?: unknown };
  return (
    (typeof name === 'string' ? failuresByName.get(name) : undefined) ??
    (typeof errorCode === 'string' ? failuresByCode.get(errorCode) : undefined)
  );
}

export function __resetSessionFlowDimensionsForTests(): void {
  clientPlatform = undefined;
  defaultCaptureTransport = undefined;
  failuresByName.clear();
  failuresByCode.clear();
}
